import { CommonModule } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  HostListener,
  computed,
  inject,
  signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatIconModule } from '@angular/material/icon';
import { ActivatedRoute, Router } from '@angular/router';
import {
  EMPTY,
  Subscription,
  catchError,
  defer,
  finalize,
  forkJoin,
  switchMap,
} from 'rxjs';

import { ToastService } from '../../../../core/services/toast.service';
import {
  EntlastungDetail,
  EntlastungEinheit,
  EntlastungSaveRequest,
  EntlastungsArtikel,
  Entlastungsgrund,
} from '../../models/entlastung.model';
import { EntlastungenLockService } from '../../services/entlastungen-lock.service';
import { InvalidEntlastungPositionError } from '../../services/entlastungen.mapper';
import { EntlastungenService } from '../../services/entlastungen.service';
import { waehleArtikelEinheit } from '../../../../shared/models/artikel-einheit.model';

type PositionFilter = 'ALLE' | 'ERFASST' | 'OFFEN';
type AddDialogStep = 'ARTIKEL' | 'GRUND';

interface EditableEntlastungsPosition {
  readonly rowId: string;
  readonly persistedId?: string;
  readonly artikelId: string;
  readonly artikelNummer: string;
  readonly artikelName: string;
  readonly warengruppe: string;
  readonly grundId: string;
  readonly einheitId: number;
  readonly einheit: string;
  readonly menge: number;
}

@Component({
  selector: 'app-entlastung-detail',
  standalone: true,
  imports: [CommonModule, MatIconModule],
  templateUrl: './entlastung-detail.component.html',
  styleUrl: './entlastung-detail.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class EntlastungDetailComponent {
  private readonly entlastungenService = inject(EntlastungenService);
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly toastService = inject(ToastService);
  private readonly lockService = inject(EntlastungenLockService);
  private readonly destroyRef = inject(DestroyRef);
  private locked = false;
  private leaving = false;
  private heartbeat?: Subscription;
  // Avoid renewing the same lock for rapid successive local-only edits.
  private readonly heartbeatMinIntervalMs = 30_000;
  private lastSuccessfulHeartbeatAt = 0;
  private heartbeatPending = false;

  private readonly filialNummer = Number(
    this.route.snapshot.paramMap.get('filialNummer')
  );

  protected readonly detail = signal<EntlastungDetail | null>(null);
  protected readonly rows = signal<EditableEntlastungsPosition[]>([]);
  private readonly removedPersistedRows = signal<EditableEntlastungsPosition[]>([]);
  protected readonly gruende = signal<Entlastungsgrund[]>([]);
  protected readonly artikel = signal<EntlastungsArtikel[]>([]);
  protected readonly isLoading = signal(true);
  protected readonly isSaving = signal(false);
  protected readonly hasUnsavedChanges = signal(false);

  protected readonly searchTerm = signal('');
  protected readonly selectedFilter = signal<PositionFilter>('ALLE');

  protected readonly activeRowId = signal<string | null>(null);
  protected readonly inputValue = signal('');
  protected readonly numpadKeys: readonly (string | null)[] = [
    '7',
    '8',
    '9',
    '4',
    '5',
    '6',
    '1',
    '2',
    '3',
    null,
    '0',
    '⌫',
  ];

  protected readonly isAddOpen = signal(false);
  protected readonly addDialogStep = signal<AddDialogStep>('ARTIKEL');
  protected readonly addQuery = signal('');
  protected readonly pendingArticle = signal<EntlastungsArtikel | null>(null);

  protected readonly activeRow = computed(() => {
    const activeId = this.activeRowId();

    if (!activeId) {
      return null;
    }

    return this.rows().find((row) => row.rowId === activeId) ?? null;
  });

  protected readonly activeAllowedReasons = computed(() => {
    const row = this.activeRow();

    if (!row) {
      return [];
    }

    return this.getAllowedReasons(row.artikelId, row.grundId);
  });

  protected readonly activeAllowedUnits = computed(() => {
    const row = this.activeRow();

    if (!row) {
      return [];
    }

    return this.getAllowedUnits(row.artikelId, row.einheitId, row.einheit);
  });

  protected readonly erfasstCount = computed(
    () => this.rows().filter((row) => row.menge > 0).length
  );

  protected readonly offenCount = computed(
    () => this.rows().filter((row) => row.menge <= 0).length
  );

  protected readonly gesamtMenge = computed(() =>
    this.rows().reduce((sum, row) => sum + Math.max(0, row.menge), 0)
  );

  protected readonly verwendeteGruendeCount = computed(
    () =>
      new Set(
        this.rows()
          .filter((row) => row.menge > 0)
          .map((row) => row.grundId)
      ).size
  );

  protected readonly displayedRows = computed(() => {
    const search = this.searchTerm().trim().toLocaleLowerCase('de');
    const filter = this.selectedFilter();

    return this.rows().filter((row) => {
      const matchesSearch =
        search.length === 0 ||
        row.artikelName.toLocaleLowerCase('de').includes(search) ||
        row.artikelNummer.toLocaleLowerCase('de').includes(search) ||
        row.warengruppe.toLocaleLowerCase('de').includes(search);

      const matchesFilter =
        filter === 'ALLE' ||
        (filter === 'ERFASST' && row.menge > 0) ||
        (filter === 'OFFEN' && row.menge <= 0);

      return matchesSearch && matchesFilter;
    });
  });

  protected readonly katalogTreffer = computed(() => {
    const query = this.addQuery().trim().toLocaleLowerCase('de');
    const items = this.artikel();

    if (!query) {
      return items.slice(0, 50);
    }

    return items
      .filter(
        (item) =>
          item.artikelName.toLocaleLowerCase('de').includes(query) ||
          item.artikelNummer.toLocaleLowerCase('de').includes(query) ||
          item.warengruppe.toLocaleLowerCase('de').includes(query)
      )
      .slice(0, 50);
  });

  protected readonly pendingReasons = computed(() => {
    const article = this.pendingArticle();

    if (!article) {
      return [];
    }

    const allowed = new Set(article.erlaubteGrundIds);
    const matches = this.gruende().filter((reason) => allowed.has(reason.id));

    return matches;
    return matches;
  });

  protected readonly datumLabel = computed(() =>
    this.formatDate(this.detail()?.datum ?? this.todayIso())
  );

  protected readonly skeletonKpis = Array.from({ length: 3 });
  protected readonly skeletonRows = Array.from({ length: 5 });

  constructor() {
    if (!Number.isFinite(this.filialNummer) || this.filialNummer <= 0) {
      void this.router.navigate(['/entlastungen']);
      return;
    }

    this.lockService.acquireLock(this.filialNummer)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => {
          this.locked = true;
          this.loadData();
        },
        error: () => {
          this.isLoading.set(false);
          void this.router.navigate(['/entlastungen']);
        },
      });
  }

  protected goBack(): void {
    if (this.isSaving()) {
      this.toastService.warning(
        'Speichern läuft',
        'Bitte warte, bis das Speichern abgeschlossen ist.'
      );
      return;
    }
    this.leaving = true;
    if (this.hasUnsavedChanges()) {
      this.toastService.warning(
        'Nicht gespeicherte Änderungen',
        'Beim Verlassen gehen deine nicht gespeicherten Entlastungen verloren.',
        0
      );
    }
    this.heartbeat?.unsubscribe();
    if (!this.lockService.getLockSession(this.filialNummer)) {
      void this.router.navigate(['/entlastungen']);
      return;
    }

    this.locked = false;
    this.lockService.releaseLock(this.filialNummer).subscribe({
      next: () => void this.router.navigate(['/entlastungen']),
      error: () => {
        this.lockService.clearLock(this.filialNummer);
        void this.router.navigate(['/entlastungen']);
      },
    });
  }

  ngOnDestroy(): void {
    this.heartbeat?.unsubscribe();
    if (!this.leaving && this.hasUnsavedChanges()) {
      this.toastService.warning(
        'Nicht gespeicherte Änderungen',
        'Die Entlastungen wurden nicht gespeichert.',
        0
      );
    }
    if (!this.leaving && this.lockService.getLockSession(this.filialNummer)) {
      this.locked = false;
      this.lockService.releaseLock(this.filialNummer).subscribe({
        error: () => this.lockService.clearLock(this.filialNummer),
      });
    }
  }

  @HostListener('window:beforeunload')
  releaseLockBeforeUnload(): void {
    this.leaving = true;
    this.heartbeat?.unsubscribe();
    if (this.lockService.getLockSession(this.filialNummer)) {
      this.locked = false;
      this.lockService.releaseLockBeforeUnload(this.filialNummer);
    }
  }

  private renewLock(): void {
    if (!this.locked || this.heartbeatPending) return;

    // A first edit still verifies/renews the lock immediately. Subsequent
    // edits within 30 seconds only update the local draft. Saving always
    // performs its own mandatory heartbeat, irrespective of this interval.
    if (
      this.lastSuccessfulHeartbeatAt !== 0 &&
      Date.now() - this.lastSuccessfulHeartbeatAt < this.heartbeatMinIntervalMs
    ) return;

    this.heartbeatPending = true;
    this.heartbeat = this.lockService.heartbeatLock(this.filialNummer)
      .pipe(
        finalize(() => { this.heartbeatPending = false; }),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe({
        next: (session) => {
          if (session === null) {
            this.handleLostLock();
          } else {
            this.lastSuccessfulHeartbeatAt = Date.now();
          }
        },
        error: (error: unknown) =>
          this.handleLostLock(this.isConfirmedLockLoss(error)),
      });
  }

  private isConfirmedLockLoss(error: unknown): boolean {
    return (
      error instanceof HttpErrorResponse &&
      (error.status === 404 || error.status === 423)
    );
  }

  private handleLostLock(clearSession = true): void {
    if (!this.locked) return;
    this.locked = false;
    this.heartbeat?.unsubscribe();
    if (clearSession) this.lockService.clearLock(this.filialNummer);
    this.toastService.warning(
      'Sperre nicht bestätigt',
      'Deine Änderungen bleiben sichtbar, können aber nicht gespeichert werden. Gehe zurück zur Übersicht und öffne die Filiale erneut.',
      0
    );
  }

  private canEdit(): boolean {
    if (this.isSaving()) {
      this.toastService.warning(
        'Speichern läuft',
        'Bitte warte, bis das Speichern abgeschlossen ist.'
      );
      return false;
    }
    if (!this.locked) {
      this.toastService.warning(
        'Sperre nicht mehr aktiv',
        'Deine Änderungen können nicht gespeichert werden. Bitte öffne die Filiale erneut.',
        0
      );
      return false;
    }
    return true;
  }

  protected setSearchTerm(value: string): void {
    this.searchTerm.set(value);
  }

  protected setFilter(filter: PositionFilter): void {
    this.selectedFilter.set(filter);
  }

  protected isFilterActive(filter: PositionFilter): boolean {
    return this.selectedFilter() === filter;
  }

  protected openNumpad(rowId: string): void {
    if (!this.canEdit()) return;
    const row = this.rows().find((entry) => entry.rowId === rowId);

    if (!row) {
      return;
    }

    this.activeRowId.set(rowId);
    this.inputValue.set('');
  }

  protected closeNumpad(): void {
    this.activeRowId.set(null);
    this.inputValue.set('');
  }

  protected pressNumpadKey(key: string): void {
    if (!this.canEdit()) return;
    if (key === '⌫') {
      this.inputValue.update((value) => value.slice(0, -1));
      return;
    }

    this.inputValue.update((value) => {
      const next = `${value}${key}`.replace(/^0+(?=\d)/, '');
      return next.slice(0, 6);
    });
  }

  protected setNumpadInput(value: string): void {
    if (!this.canEdit()) return;
    this.inputValue.set(value.replace(/[^0-9]/g, '').slice(0, 6));
  }

  protected stepMenge(delta: number): void {
    if (!this.canEdit()) return;
    const active = this.activeRow();

    if (!active) {
      return;
    }

    const source = this.inputValue().trim();
    const current = source ? Number.parseInt(source, 10) : active.menge;
    const next = Math.max(0, (Number.isFinite(current) ? current : 0) + delta);

    this.inputValue.set(String(next));
  }

  protected confirmQuantity(): void {
    if (!this.canEdit()) return;
    this.commitActiveQuantity();
    this.closeNumpad();
  }

  protected openAddDialogFromNumpad(): void {
    if (!this.canEdit()) return;
    this.commitActiveQuantity();
    this.closeNumpad();
    this.openAddDialog();
  }

  protected openAddDialog(): void {
    if (!this.canEdit()) return;
    this.addDialogStep.set('ARTIKEL');
    this.addQuery.set('');
    this.pendingArticle.set(null);
    this.isAddOpen.set(true);
  }

  protected closeAddDialog(): void {
    this.isAddOpen.set(false);
    this.pendingArticle.set(null);
    this.addQuery.set('');
    this.addDialogStep.set('ARTIKEL');
  }

  protected chooseArticle(article: EntlastungsArtikel): void {
    if (!this.canEdit()) return;
    this.pendingArticle.set(article);
    this.addDialogStep.set('GRUND');
  }

  protected backToArticleSearch(): void {
    this.pendingArticle.set(null);
    this.addDialogStep.set('ARTIKEL');
  }

  protected setAddQuery(value: string): void {
    this.addQuery.set(value);
  }

  protected addWithReason(reasonId: string): void {
    if (!this.canEdit()) return;
    const article = this.pendingArticle();
    const unit = waehleArtikelEinheit(article?.erlaubteEinheiten ?? [],
      article?.erlaubteEinheiten.some((entry) => entry.isDefault) ? null : article?.grundEinheitId);

    if (!article || !unit) {
      return;
    }

    const existing = this.rows().find(
      (row) =>
        row.artikelId === article.id &&
        row.grundId === reasonId &&
        row.einheitId === unit.id
    );

    if (existing) {
      this.closeAddDialog();
      this.openNumpad(existing.rowId);
      return;
    }

    const rowId = this.createRowId(article.id, reasonId, unit.id);
    const next: EditableEntlastungsPosition = {
      rowId,
      artikelId: article.id,
      artikelNummer: article.artikelNummer,
      artikelName: article.artikelName,
      warengruppe: article.warengruppe,
      grundId: reasonId,
      einheitId: unit.id,
      einheit: unit.bezeichnung,
      menge: 0,
    };

    this.rows.update((rows) => [next, ...rows]);
    this.markDirty();
    this.closeAddDialog();
    this.openNumpad(rowId);
  }

  protected changeReason(rowId: string, reasonId: string): void {
    if (!this.canEdit()) return;
    this.patchRow(rowId, { grundId: reasonId });
  }

  protected changeUnit(rowId: string, einheitIdValue: string | number): void {
    if (!this.canEdit()) return;
    const einheitId = Number(einheitIdValue);
    const row = this.rows().find((entry) => entry.rowId === rowId);

    if (!row || !Number.isFinite(einheitId)) {
      return;
    }

    const unit = this.getAllowedUnits(
      row.artikelId,
      row.einheitId,
      row.einheit
    ).find((entry) => entry.id === einheitId);

    if (!unit) {
      return;
    }

    this.patchRow(rowId, {
      einheitId: unit.id,
      einheit: unit.bezeichnung,
    });
  }

  protected removeRow(rowId: string): void {
    if (!this.canEdit()) return;
    const removed = this.rows().find((row) => row.rowId === rowId);
    if (!removed) return;

    if (removed.persistedId) {
      this.removedPersistedRows.update((rows) =>
        rows.some((row) => row.persistedId === removed.persistedId)
          ? rows
          : [...rows, removed]
      );
    }
    this.rows.update((rows) => rows.filter((row) => row.rowId !== rowId));

    if (this.activeRowId() === rowId) {
      this.closeNumpad();
    }

    this.markDirty();
  }

  protected getReasonName(reasonId: string): string {
    return this.gruende().find((reason) => reason.id === reasonId)?.name ?? '–';
  }

  protected getAllowedReasons(
    artikelId: string,
    currentReasonId?: string
  ): Entlastungsgrund[] {
    const article = this.artikel().find((item) => item.id === artikelId);

    if (!article) {
      return currentReasonId
        ? this.gruende().filter((reason) => reason.id === currentReasonId)
        : [];
      return currentReasonId
        ? this.gruende().filter((reason) => reason.id === currentReasonId)
        : [];
    }

    const allowed = new Set(article.erlaubteGrundIds);
    return this.gruende().filter(
      (reason) => allowed.has(reason.id) || reason.id === currentReasonId
    );
  }

  protected getAllowedUnits(
    artikelId: string,
    currentUnitId?: number,
    currentUnitName?: string
  ): EntlastungEinheit[] {
    const article = this.artikel().find((item) => item.id === artikelId);
    const units = article?.erlaubteEinheiten ? [...article.erlaubteEinheiten] : [];

    if (
      currentUnitId !== undefined &&
      currentUnitName &&
      !units.some((unit) => unit.id === currentUnitId)
    ) {
      units.push({ id: currentUnitId, bezeichnung: currentUnitName });
    }

    return units;
  }

  protected isActiveReason(reasonId: string): boolean {
    return this.activeRow()?.grundId === reasonId;
  }

  protected isActiveUnit(unitId: number): boolean {
    return this.activeRow()?.einheitId === unitId;
  }

  protected selectActiveReason(reasonId: string): void {
    const row = this.activeRow();

    if (row) {
      this.changeReason(row.rowId, reasonId);
    }
  }

  protected selectActiveUnit(unitId: number): void {
    const row = this.activeRow();

    if (row) {
      this.changeUnit(row.rowId, unitId);
    }
  }

  protected save(): void {
    const detail = this.detail();

    if (!detail || this.isSaving()) return;
    if (!this.locked || !this.lockService.getLockSession(this.filialNummer)) {
      if (this.locked) {
        this.handleLostLock();
      } else {
        this.toastService.warning(
          'Sperre nicht mehr aktiv',
          'Deine Änderungen können nicht gespeichert werden. Bitte öffne die Filiale erneut.',
          0
        );
      }
      return;
    }

    this.commitActiveQuantity();
    this.closeNumpad();
    if (detail.datum !== this.todayIso()) {
      this.toastService.warning(
        'Tag gewechselt',
        'Diese Entlastungen stammen nicht von heute. Bitte öffne die Filiale erneut; deine Änderungen werden nicht gespeichert.',
        0
      );
      return;
    }
    this.isSaving.set(true);

    const request: EntlastungSaveRequest = {
      datum: detail.datum,
      positionen: [
        ...this.rows().map((row) => ({
          id: row.persistedId ?? null,
          artikelId: row.artikelId,
          grundId: row.grundId,
          einheitId: row.einheitId,
          menge: row.menge,
        })),
        ...this.removedPersistedRows().map((row) => ({
          id: row.persistedId ?? null,
          artikelId: row.artikelId,
          grundId: row.grundId,
          einheitId: row.einheitId,
          menge: 0,
        })),
      ],
    };

    this.heartbeat?.unsubscribe();
    const renewal = this.lockService.heartbeatLock(this.filialNummer);
    renewal.pipe(
      catchError((error: unknown) => {
        this.handleLostLock(this.isConfirmedLockLoss(error));
        return EMPTY;
      }),
      switchMap((session) => {
        if (!this.locked || session === null) {
          this.handleLostLock();
          return EMPTY;
        }
        if (detail.datum !== this.todayIso()) {
          this.toastService.warning(
            'Tag gewechselt',
            'Die Entlastungen konnten nicht gespeichert werden. Bitte öffne die Filiale erneut.',
            0
          );
          return EMPTY;
        }
        return defer(() => this.entlastungenService.saveEntlastung(this.filialNummer, request));
      }),
      switchMap(() => this.entlastungenService.getDetail(this.filialNummer)),
      finalize(() => this.isSaving.set(false)),
      takeUntilDestroyed(this.destroyRef)
    ).subscribe({
      next: (saved) => {
        if (!this.locked) return;
        if (saved.datum !== detail.datum) {
          this.toastService.warning(
            'Tag gewechselt',
            'Die Änderungen bleiben sichtbar. Bitte öffne die Filiale erneut.',
            0
          );
          return;
        }
        this.detail.set(saved);
        this.rows.set(this.mapDetailRows(saved));
        this.removedPersistedRows.set([]);
        this.hasUnsavedChanges.set(false);
        this.toastService.success(
          'Entlastungen gespeichert',
          `${saved.positionen.length} Positionen wurden erfasst.`
        );
      },
      error: (error: unknown) => {
        if (error instanceof HttpErrorResponse && error.status === 423) {
          this.handleLostLock();
          return;
        }
        if (error instanceof InvalidEntlastungPositionError) {
          const field = error.field === 'grundId' ? 'Entlastungsgrund' :
            error.field === 'einheitId' ? 'Einheit' :
            error.field === 'menge' ? 'Menge' : 'Artikel';
          this.toastService.error(
            'Speichern fehlgeschlagen',
            `Eine Position enthält ungültige Angaben (${field}). Bitte korrigiere sie; alle Änderungen bleiben erhalten.`
          );
          return;
        }
        this.toastService.error(
          'Speichern fehlgeschlagen',
          'Die Entlastungen konnten nicht gespeichert werden.'
        );
      },
    });
  }

  protected formatMenge(value: number): string {
    return new Intl.NumberFormat('de-DE', {
      maximumFractionDigits: 0,
    }).format(Math.max(0, value));
  }

  private loadData(): void {
    this.isLoading.set(true);

    forkJoin({
      detail: this.entlastungenService.getDetail(this.filialNummer),
      gruende: this.entlastungenService.getGruende(this.filialNummer),
      artikel: this.entlastungenService.getVerfuegbareArtikel(
        this.filialNummer
      ),
    })
      .pipe(
        finalize(() => this.isLoading.set(false)),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe({
        next: ({ detail, gruende, artikel }) => {
          this.detail.set(detail);
          this.gruende.set(gruende);
          this.artikel.set(artikel);
          this.rows.set(this.mapDetailRows(detail));
          this.removedPersistedRows.set([]);
          this.hasUnsavedChanges.set(false);
        },
        error: (error: unknown) => {
          if (error instanceof HttpErrorResponse && error.status === 423) {
            this.handleLostLock();
          } else {
            this.toastService.error(
              'Entlastungen konnten nicht geladen werden',
              'Bitte versuche es erneut.'
            );
          }
          this.goBack();
        },
      });
  }

  private mapDetailRows(
    detail: EntlastungDetail
  ): EditableEntlastungsPosition[] {
    return detail.positionen.map((position) => ({
      rowId: this.createRowId(
        position.artikelId,
        position.grundId,
        position.einheitId,
        position.id
      ),
      persistedId: position.id,
      artikelId: position.artikelId,
      artikelNummer: position.artikelNummer,
      artikelName: position.artikelName,
      warengruppe: position.warengruppe,
      grundId: position.grundId,
      einheitId: position.einheitId,
      einheit: position.einheit,
      menge: Math.max(0, Math.round(position.menge)),
    }));
  }

  private patchRow(
    rowId: string,
    patch: Partial<
      Pick<
        EditableEntlastungsPosition,
        'grundId' | 'einheitId' | 'einheit' | 'menge'
      >
    >
  ): void {
    let nextActiveRowId = rowId;

    this.rows.update((rows) => {
      const current = rows.find((row) => row.rowId === rowId);

      if (!current) {
        return rows;
      }

      const next = {
        ...current,
        ...patch,
        menge:
          patch.menge === undefined
            ? current.menge
            : Math.max(0, Math.round(patch.menge)),
      };

      const duplicate = rows.find(
        (row) =>
          row.rowId !== rowId &&
          row.artikelId === next.artikelId &&
          row.grundId === next.grundId &&
          row.einheitId === next.einheitId
      );

      if (duplicate) {
        nextActiveRowId = duplicate.rowId;

        return rows
          .filter((row) => row.rowId !== rowId)
          .map((row) =>
            row.rowId === duplicate.rowId
              ? { ...row, menge: row.menge + next.menge }
              : row
          );
      }

      return rows.map((row) => (row.rowId === rowId ? next : row));
    });

    if (this.activeRowId() === rowId && nextActiveRowId !== rowId) {
      this.activeRowId.set(nextActiveRowId);
    }

    this.markDirty();
  }

  private commitActiveQuantity(): void {
    const row = this.activeRow();

    if (!row) {
      return;
    }

    const value = this.inputValue().trim();

    if (!value) {
      return;
    }

    const quantity = Number.parseInt(value, 10);

    if (!Number.isFinite(quantity)) {
      return;
    }

    this.patchRow(row.rowId, { menge: quantity });
    this.inputValue.set('');
  }

  private markDirty(): void {
    this.hasUnsavedChanges.set(true);
    this.renewLock();
  }

  private createRowId(
    artikelId: string,
    grundId: string,
    einheitId: number,
    stableId?: string
  ): string {
    return stableId ?? `${artikelId}-${grundId}-${einheitId}-${Date.now()}-${Math.random()}`;
  }

  private todayIso(): string {
    const today = new Date();
    const year = today.getFullYear();
    const month = String(today.getMonth() + 1).padStart(2, '0');
    const day = String(today.getDate()).padStart(2, '0');

    return `${year}-${month}-${day}`;
  }

  private formatDate(value: string): string {
    const [year, month, day] = value.split('-');

    return `${day}.${month}.${year}`;
  }
}
